// Reading a filter tree out of the DOM and serializing it for the query string.
//
// Pure helpers with no init() of their own: the builder owns the markup and
// calls these, so the reading rule lives in one place and the builder stays
// about interaction.
//
// The tree travels as **one opaque `filter` parameter carrying URL-encoded
// JSON**. It cannot travel as repeated GET fields the way every other filter in
// the app does: liveList.js builds its query with `params.set` over form
// values, which collapses duplicates, so a nested AND/OR structure would be
// lossy twice over. A POST with a JSON body was rejected outright - it destroys
// the bookmarkable URL, forces a second non-renderList path, and makes
// X-Fragment illegal, since wantsFragment is GET-only by design.

// Reads one condition row into a rule object, or null when it is not finished
// enough to mean anything.
//
// **An incomplete row is omitted rather than serialized**, so the results keep
// updating sensibly while somebody is halfway through building a row.
export function leseBedingung(el) {
  const feld = el.querySelector('[data-rolle="feld"]');
  const operator = el.querySelector('[data-rolle="operator"]');

  if (!feld || !feld.value || !operator || !operator.value) return null;

  const regel = { feld: feld.value, operator: operator.value };

  // The value controls, by shape. Only one of these is present per row - the
  // server rendered whichever the operator takes.
  const fenster = el.querySelector('[data-rolle="fenster"]');
  if (fenster) {
    if (!fenster.value) return null;
    regel.fenster = fenster.value;
    return regel;
  }

  const von = el.querySelector('[data-rolle="von"]');
  const bis = el.querySelector('[data-rolle="bis"]');
  if (von || bis) {
    const vonWert = von ? von.value : '';
    const bisWert = bis ? bis.value : '';
    if (!vonWert && !bisWert) return null;
    if (vonWert) regel.von = vonWert;
    if (bisWert) regel.bis = bisWert;
    return regel;
  }

  // A checkbox group repeating data-rolle="wert" - the multi-value rule kept
  // inside the row, so the group still reads as an array.
  const kaestchen = el.querySelectorAll('input[type="checkbox"][data-rolle="wert"]');
  if (kaestchen.length > 0) {
    const werte = [...kaestchen].filter((k) => k.checked).map((k) => k.value);
    if (werte.length === 0) return null;
    regel.werte = werte;
    return regel;
  }

  const wert = el.querySelector('[data-rolle="wert"]');
  if (wert) {
    if (wert.value === '') return null;
    regel.wert = wert.value;
    return regel;
  }

  // An operator taking no value at all - "ist leer", "ist ja".
  return regel;
}

/**
 * Reads a group element and everything under it.
 *
 * Recursive, and it reads only its *own* direct children: a nested group is
 * read by the same function one level down, so the two cannot disagree about
 * what a group is.
 *
 * @param {Element} el the group element
 * @returns {object|null} null when the group has nothing complete in it
 */
export function leseGruppe(el) {
  if (!el) return null;

  const verknuepfungEl = el.querySelector(
    ':scope > * [data-rolle="verknuepfung"], :scope > [data-rolle="verknuepfung"]'
  );
  const verknuepfung = verknuepfungEl && verknuepfungEl.value === 'oder' ? 'oder' : 'und';

  const behaelter = el.querySelector(':scope > [data-rolle="regeln"]');
  if (!behaelter) return null;

  const regeln = [];

  [...behaelter.children].forEach((kind) => {
    if (kind.matches('[data-rolle="gruppe"]')) {
      const unter = leseGruppe(kind);
      if (unter) regeln.push(unter);
      return;
    }

    if (kind.matches('[data-rolle="bedingung"]')) {
      const regel = leseBedingung(kind);
      if (regel) regeln.push(regel);
    }
  });

  if (regeln.length === 0) return null;

  return { verknuepfung, regeln };
}

/**
 * Serializes a tree for the query string.
 *
 * Returns '' for an empty tree, which is what makes liveList.js drop the
 * parameter rather than leaving `filter={}` sitting in the address bar.
 */
export function serialisiere(baum) {
  if (!baum || !baum.regeln || baum.regeln.length === 0) return '';
  return JSON.stringify(baum);
}

// Parses a tree back out of a query string or a data attribute, tolerating
// anything malformed: a broken `filter` parameter someone edited by hand should
// leave an empty builder rather than a broken page.
export function parse(text) {
  if (!text) return null;
  try {
    const baum = JSON.parse(text);
    return baum && Array.isArray(baum.regeln) ? baum : null;
  } catch {
    return null;
  }
}
